import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AlertTriangle, ChevronDown, Clock, Filter as FilterIcon, PauseCircle, PlayCircle, RotateCcw, XCircle } from 'lucide-react';
import type { BatchDetail, ResultCategory } from '@autopilot/contracts';
import { DEFAULT_MAX_ERROR_RISK, DEFAULT_MIN_CORRECTNESS } from '@autopilot/contracts';
import { api } from '../api/client';
import { qk, useNameLookup, useRoles } from '../api/hooks';
import { isConflict } from '../api/errors';
import { ModeBadge, PhaseBadge } from '../components/Badges';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageHeader } from '../components/PageHeader';
import { ErrorAlert, QueryBoundary } from '../components/States';
import { TokenValue } from '../components/TokenValue';
import { SelectionPreview, SelectionProgress } from '../features/batch/SelectionPanel';
import { PreheatConfig, PreheatProgress } from '../features/batch/PreheatPanel';
import { ReviewControls, ScoreDistributions } from '../features/batch/ReviewPanel';
import { TicketTable, type TableFilter, FILTER_LABEL } from '../features/batch/TicketTable';
import { TicketModal } from '../features/batch/TicketModal';
import { ExecutionPanel, SummaryPanel } from '../features/batch/ExecutionPanel';
import { CLOSE_REASON_LABEL } from '../lib/labels';
import { formatDateTime, shortId } from '../lib/format';
import { summarizeFilter } from '../lib/filters';
import { useBackoffInterval } from '../lib/polling';

const RUNNING_PHASES = new Set(['SELECTING', 'PREHEATING', 'EXECUTING']);
const FINISHED_PHASES = new Set(['COMPLETED', 'CANCELLED', 'FAILED', 'NEEDS_ATTENTION']);
const CANCELLABLE_PHASES = new Set(['SELECTING', 'PREVIEW_READY', 'PREHEATING', 'REVIEW_READY', 'PAUSED']);

export function authorizationText(b: Pick<BatchDetail, 'trigger' | 'createdBy' | 'policyVersion'>): string {
  if (b.trigger === 'SCHEDULED') return `System (Regel v${b.policyVersion ?? '?'})`;
  return b.createdBy?.name ?? 'Unbekannt';
}

export function BatchPage() {
  const { batchId = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isOperator } = useRoles();
  const names = useNameLookup();
  const [active, setActive] = useState(true);
  const interval = useBackoffInterval(active);

  const q = useQuery({
    queryKey: qk.batch(batchId),
    queryFn: () => api.getBatch(batchId),
    refetchInterval: interval,
    refetchIntervalInBackground: true,
  });
  const batch = q.data;
  useEffect(() => {
    setActive(!!batch && RUNNING_PHASES.has(batch.phase));
  }, [batch]);

  const lastRun = batch?.executionRuns.at(-1);
  const [min, setMin] = useState<number>(DEFAULT_MIN_CORRECTNESS);
  const [max, setMax] = useState<number>(DEFAULT_MAX_ERROR_RISK);
  useEffect(() => {
    if (lastRun) {
      setMin(lastRun.minCorrectness);
      setMax(lastRun.maxErrorRisk);
    }
  }, [lastRun?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableFilter = (search.get('filter') as TableFilter | null) ?? 'all';
  const resultCategory = search.get('category') ?? undefined;
  const openTicket = search.get('ticket');
  const setParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setSearch(next, { replace: true });
  };

  const lifecycle = useMutation({
    mutationFn: (action: 'stop' | 'resume' | 'cancel') => {
      const body = { expectedRowVersion: batch!.rowVersion };
      if (action === 'resume') return api.resumeBatch(batchId, body);
      if (action === 'cancel') return api.cancelBatch(batchId, body);
      return api.stopBatch(batchId, body);
    },
    onSuccess: (b) => {
      qc.setQueryData(qk.batch(batchId), b);
      setConfirmCancel(false);
      setActive(true);
    },
    onError: (err) => {
      if (isConflict(err)) void qc.invalidateQueries({ queryKey: qk.batch(batchId) });
    },
  });
  const [confirmCancel, setConfirmCancel] = useState(false);
  const lifecycleAction = lifecycle.variables === 'resume' ? 'Fortsetzen' : lifecycle.variables === 'cancel' ? 'Abbrechen' : 'Anhalten';

  return (
    <QueryBoundary query={q} action="Batch laden" what="diesen Batch" loadingRows={8}>
      {batch && (
        <>
          <PageHeader
            eyebrow={batch.trigger === 'SCHEDULED' ? 'Automatischer Batch' : 'Manueller Batch'}
            title={batch.name ?? `Batch ${shortId(batch.id)}`}
            description={
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
                <ModeBadge trigger={batch.trigger} />
                <PhaseBadge phase={batch.phase} />
                <Typography variant="body2" component="span">
                  {batch.trigger === 'SCHEDULED' ? `Auslöser: Zeitplan · ${authorizationText(batch)}` : `Erstellt von ${authorizationText(batch)}`} · {formatDateTime(batch.createdAt)}
                </Typography>
              </Box>
            }
            actions={
              isOperator && batch.trigger === 'MANUAL' ? (
                <>
                  {CANCELLABLE_PHASES.has(batch.phase) && (
                    <Button startIcon={<XCircle size={16} />} onClick={() => setConfirmCancel(true)} data-testid="cancel-batch">
                      Batch abbrechen
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<RotateCcw size={16} />} onClick={() => nav('/batches/new', { state: { filter: batch.filter } })}>
                    Neue Auswahl mit diesen Filtern
                  </Button>
                </>
              ) : undefined
            }
          />
          <Stack spacing={2.5}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }} data-testid="filter-summary">
                  <FilterIcon size={15} aria-hidden /> {summarizeFilter(batch.filter, names)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Modell: {batch.generator ? `${batch.generator.name} (${batch.generator.modelId})` : 'noch nicht gewählt'}
                </Typography>
                {batch.reviewProfile && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Prüfprofil: {batch.reviewProfile.name} v{batch.reviewProfile.version}
                  </Typography>
                )}
                <Typography variant="body2" component="div" sx={{ color: 'text.secondary' }}>
                  Tokens: <TokenValue usage={batch.usage} />
                </Typography>
                {batch.trigger === 'SCHEDULED' && batch.selection?.createdAtLte && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Clock size={14} aria-hidden /> Tickets erstellt bis {formatDateTime(batch.selection.createdAtLte)} (Mindestalter 30 Min.)
                  </Typography>
                )}
              </CardContent>
            </Card>

            {batch.contentExpired && (
              <Alert severity="info" data-testid="content-expired">
                <strong>Inhalt abgelaufen.</strong> Ticketkontexte und Antworttexte wurden gemäß Aufbewahrungsfrist gelöscht. Kennzahlen und Ergebnisse bleiben innerhalb ihrer Frist sichtbar; eine erneute Ausführung ist nicht möglich.
              </Alert>
            )}
            {batch.phase !== 'PAUSED' && (batch.stateReason || (batch.closeReason && batch.closeReason !== 'COMPLETED')) && (
              <Alert severity={batch.phase === 'NEEDS_ATTENTION' || batch.phase === 'FAILED' ? 'error' : 'info'} icon={batch.phase === 'NEEDS_ATTENTION' || batch.phase === 'FAILED' ? <AlertTriangle size={20} /> : undefined}>
                {batch.closeReason && batch.closeReason !== 'COMPLETED' && <strong>{CLOSE_REASON_LABEL[batch.closeReason]}. </strong>}
                {batch.stateReason}
              </Alert>
            )}
            <ErrorAlert error={lifecycle.error} action={lifecycleAction} onClose={() => lifecycle.reset()} />

            {batch.phase === 'SELECTING' && <SelectionProgress onCancel={() => setConfirmCancel(true)} cancelling={lifecycle.isPending} canCancel={isOperator} />}
            {batch.phase === 'PAUSED' && (
              <Card component="section" aria-labelledby="paused-title" sx={{ borderColor: 'var(--color-warning)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography id="paused-title" variant="h5" component="h2" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <PauseCircle size={20} aria-hidden /> Batch angehalten
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2 }}>
                    Es werden keine neuen Modellaufrufe gestartet. Bereits laufende Aufrufe können noch Tokens verbrauchen. Fortsetzen verarbeitet nur die fehlenden Schritte desselben Stands.
                    {batch.closeReason === 'BUDGET_EXCEEDED' || batch.closeReason === 'TIME_LIMIT' ? ' Ein Admin kann das Budget anpassen und den Batch fortsetzen.' : ''}
                    {batch.closeReason ? ` Grund: ${CLOSE_REASON_LABEL[batch.closeReason]}.` : ''}
                  </Typography>
                  {isOperator && batch.trigger === 'MANUAL' && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button variant="contained" startIcon={<PlayCircle size={18} />} onClick={() => lifecycle.mutate('resume')} disabled={lifecycle.isPending} data-testid="resume-batch">
                        Fortsetzen
                      </Button>
                      <Button onClick={() => setConfirmCancel(true)} disabled={lifecycle.isPending}>
                        Batch abbrechen
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {batch.selection?.status === 'READY' &&
              (batch.phase === 'PREVIEW_READY' ? (
                <SelectionPreview batch={batch} />
              ) : (
                <Accordion disableGutters sx={{ borderRadius: '16px !important', border: '1px solid #E1E5EC', boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ChevronDown size={18} />}>
                    <Typography variant="subtitle1">
                      Auswahl: {batch.selection.counts?.usable ?? 0} nutzbare von {batch.selection.counts?.raw ?? 0} Tickets
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SelectionPreview batch={batch} />
                  </AccordionDetails>
                </Accordion>
              ))}
            {batch.selection?.status === 'FAILED' && (
              <Alert severity="error" icon={<AlertTriangle size={20} />}>
                Die Auswahl konnte nicht erstellt werden. Erstellen Sie mit denselben Filtern eine neue Auswahl.
              </Alert>
            )}

            {batch.phase === 'PREVIEW_READY' && batch.trigger === 'MANUAL' && isOperator && <PreheatConfig batch={batch} />}
            {batch.phase === 'PREHEATING' && <PreheatProgress batch={batch} onStop={() => lifecycle.mutate('stop')} stopping={lifecycle.isPending} />}

            {FINISHED_PHASES.has(batch.phase) && batch.progress.total > 0 && (
              <SummaryPanel
                batch={batch}
                onShow={(f, c: ResultCategory) => {
                  setParam({ filter: f, category: c });
                  document.getElementById('ticket-table')?.scrollIntoView({ behavior: 'smooth' });
                }}
              />
            )}
            {lastRun && <ExecutionPanel key={lastRun.id} batch={batch} run={lastRun} />}
            {batch.executionRuns.length > 1 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Frühere Ausführungsläufe: {batch.executionRuns.slice(0, -1).map((r) => `${formatDateTime(r.createdAt)} (${r.state})`).join(' · ')}
              </Typography>
            )}

            {(batch.phase === 'REVIEW_READY' || batch.phase === 'EXECUTING' || ((FINISHED_PHASES.has(batch.phase) || batch.phase === 'PAUSED') && batch.progress.done > 0)) && (
              <>
                <Card component="section" aria-labelledby="dist-title">
                  <CardContent sx={{ p: 3 }}>
                    <Typography id="dist-title" variant="h5" component="h2" sx={{ mb: 2 }}>
                      Bewertungen
                    </Typography>
                    <ScoreDistributions batch={batch} />
                  </CardContent>
                </Card>
                {batch.phase === 'REVIEW_READY' && (
                  <ReviewControls
                    batch={batch}
                    minCorrectness={min}
                    maxErrorRisk={max}
                    onChange={(a, b) => {
                      setMin(a);
                      setMax(b);
                    }}
                    canStart={batch.trigger === 'MANUAL' && !batch.contentExpired}
                    onStarted={() => setActive(true)}
                  />
                )}
                {resultCategory && (
                  <Box>
                    <Button size="small" onClick={() => setParam({ category: null, filter: null })}>
                      Ergebnisfilter entfernen ({FILTER_LABEL[tableFilter]})
                    </Button>
                  </Box>
                )}
                <TicketTable
                  batchId={batch.id}
                  batchRowVersion={batch.rowVersion}
                  minCorrectness={min}
                  maxErrorRisk={max}
                  filter={tableFilter}
                  resultCategory={resultCategory}
                  onFilterChange={(f) => setParam({ filter: f === 'all' ? null : f, category: null })}
                  onOpen={(id) => setParam({ ticket: id })}
                  canEditExclusion={batch.phase === 'REVIEW_READY' && batch.trigger === 'MANUAL'}
                  live={batch.phase === 'EXECUTING'}
                />
              </>
            )}
          </Stack>
          <TicketModal ticketId={openTicket} batch={batch} onClose={() => setParam({ ticket: null })} />
          <ConfirmDialog
            open={confirmCancel}
            title="Batch abbrechen?"
            confirmLabel="Batch abbrechen"
            cancelLabel="Zurück"
            destructive
            busy={lifecycle.isPending}
            onCancel={() => setConfirmCancel(false)}
            onConfirm={() => lifecycle.mutate('cancel')}
          >
            Der Batch wird endgültig beendet; es werden keine weiteren Schritte ausgeführt und nichts versendet. Bereits verbrauchte Tokens bleiben ausgewiesen. Eine neue Auswahl ist jederzeit möglich.
          </ConfirmDialog>
        </>
      )}
    </QueryBoundary>
  );
}
